import posthog from "posthog-js";

type BrandLike = {
  id?: string;
  brandId?: string;
  country?: string | null;
};

type CompanyLike = {
  id?: string;
  companyId?: string;
  company_type?: string | null;
  name?: string;
  url?: string;
};

type UserLike = {
  id?: string;
  email?: string;
  tier?: string;
  role?: string;
  auth_provider?: string;
  authProvider?: string;
  brands?: BrandLike[];
  company?: CompanyLike;
};

type ReferralAttribution = {
  referral_code?: string;
  referral_partner?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  landing_path?: string;
  landing_url?: string;
  referrer_domain?: string;
  first_seen_at?: string;
};

const POSTHOG_KEY = String(import.meta.env.VITE_POSTHOG_KEY || "").trim();
const POSTHOG_HOST = String(
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
).trim();
const REFERRAL_ATTRIBUTION_KEY = "nobori_referral_attribution_v1";
const ONBOARDING_STARTED_AT_KEY = "nobori_onboarding_started_at_v1";
const ONBOARDING_COMPLETED_AT_PREFIX = "nobori_onboarding_completed_at_v1";
const DASHBOARD_FIRST_VIEW_PREFIX = "nobori_dashboard_first_view_v1";

let initialized = false;

const cleanProperties = (input?: Record<string, unknown>) => {
  if (!input) return {};
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  );
};

const inferCompanyType = (brandsCount: number): string => {
  if (brandsCount > 1) return "agency";
  if (brandsCount === 1) return "brand";
  return "unknown";
};

const readQueryValue = (params: URLSearchParams, keys: string[]): string => {
  for (const key of keys) {
    const value = params.get(key);
    if (value && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const getReferrerDomain = (): string => {
  if (typeof document === "undefined" || !document.referrer) return "";
  try {
    return new URL(document.referrer).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
};

const readStoredReferralAttribution = (): ReferralAttribution | null => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(REFERRAL_ATTRIBUTION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ReferralAttribution;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveReferralAttribution = (value: ReferralAttribution) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(REFERRAL_ATTRIBUTION_KEY, JSON.stringify(value));
};

const resolveSignupSource = (
  signupSource?: string | null,
  referralAttribution?: ReferralAttribution | null,
): string => {
  const normalizedSource = String(signupSource || "")
    .trim()
    .toLowerCase();
  if (normalizedSource === "invite") return "invite";
  if (referralAttribution) return "referral";
  if (normalizedSource === "marketing_home") return "marketing_home";
  return "direct";
};

const resolveAuthMethod = (value?: string | null): "google" | "email" => {
  const provider = String(value || "")
    .trim()
    .toLowerCase();
  if (provider.includes("google")) return "google";
  return "email";
};

const normalizeProvider = (provider?: string | null): string => {
  const value = String(provider || "").trim().toLowerCase();
  if (!value) return "unknown";
  if (value.includes("copilot")) {
    return "copilot";
  }
  if (value.includes("grok")) {
    return "grok";
  }
  if (value.includes("ai_mode") || value.includes("ai-mode")) {
    return "ai_mode";
  }
  if (value.includes("perp") || value.includes("perplexity")) return "perplexity";
  if (
    value.includes("google") ||
    value.includes("overview") ||
    value.includes("gemini")
  ) {
    return "gemini";
  }
  if (value.includes("gpt")) {
    return "chatgpt";
  }
  return value;
};

export const toPlatformValue = (providers?: string[] | string | null): string => {
  const normalized = Array.isArray(providers)
    ? providers
    : typeof providers === "string"
      ? providers.split(",")
      : [];
  const values = Array.from(
    new Set(
      normalized
        .map((provider) => normalizeProvider(provider))
        .filter((provider) => provider && provider !== "unknown"),
    ),
  );
  if (!values.length) return "all";
  if (values.length === 1) return values[0];
  return values.join(",");
};

export const toDomainType = (value?: string | null): string => {
  const type = String(value || "").trim().toLowerCase();
  if (!type) return "unknown";
  if (type.includes("competitor")) return "competitor";
  if (type.includes("publisher") || type.includes("media")) return "media";
  if (type.includes("community") || type.includes("blog") || type.includes("social")) {
    return "blog";
  }
  if (type.includes("directory") || type.includes("owned")) return "directory";
  return "unknown";
};

export const extractDomain = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    const normalized = value.includes("://") ? value : `https://${value}`;
    return new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return value
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split(/[/?#:]/)[0]
      .toLowerCase();
  }
};

export const initPosthog = () => {
  if (initialized || !POSTHOG_KEY || typeof window === "undefined") return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: false,
    persistence: "localStorage+cookie",
  });
  initialized = true;
};

export const captureReferralAttributionFromUrl = (): ReferralAttribution | null => {
  if (typeof window === "undefined") return null;

  const existing = readStoredReferralAttribution();
  const params = new URLSearchParams(window.location.search);
  const referralCode = readQueryValue(params, [
    "ref",
    "referral",
    "ref_code",
    "referral_code",
    "affiliate",
    "aff",
    "r",
  ]);
  const referralPartner = readQueryValue(params, [
    "partner",
    "affiliate_partner",
    "ref_partner",
  ]);
  const utmSource = readQueryValue(params, ["utm_source"]);
  const utmMedium = readQueryValue(params, ["utm_medium"]);
  const utmCampaign = readQueryValue(params, ["utm_campaign"]);
  const utmTerm = readQueryValue(params, ["utm_term"]);
  const utmContent = readQueryValue(params, ["utm_content"]);
  const gclid = readQueryValue(params, ["gclid"]);
  const fbclid = readQueryValue(params, ["fbclid"]);
  const hasAttribution = Boolean(
    referralCode ||
      referralPartner ||
      utmSource ||
      utmMedium ||
      utmCampaign ||
      utmTerm ||
      utmContent ||
      gclid ||
      fbclid,
  );
  if (!hasAttribution) return existing;

  const landingUrl = window.location.href || "";
  const landingPath = window.location.pathname || "";

  if (
    existing &&
    existing.referral_code === (referralCode || undefined) &&
    existing.referral_partner === (referralPartner || undefined) &&
    existing.utm_source === (utmSource || undefined) &&
    existing.utm_medium === (utmMedium || undefined) &&
    existing.utm_campaign === (utmCampaign || undefined) &&
    existing.utm_term === (utmTerm || undefined) &&
    existing.utm_content === (utmContent || undefined) &&
    existing.gclid === (gclid || undefined) &&
    existing.fbclid === (fbclid || undefined) &&
    existing.landing_url === (landingUrl || undefined)
  ) {
    return existing;
  }

  const attribution = cleanProperties({
    referral_code: referralCode || undefined,
    referral_partner: referralPartner || undefined,
    utm_source: utmSource || undefined,
    utm_medium: utmMedium || undefined,
    utm_campaign: utmCampaign || undefined,
    utm_term: utmTerm || undefined,
    utm_content: utmContent || undefined,
    gclid: gclid || undefined,
    fbclid: fbclid || undefined,
    landing_path: landingPath || undefined,
    landing_url: landingUrl || undefined,
    referrer_domain: getReferrerDomain() || undefined,
    first_seen_at: new Date().toISOString(),
  }) as ReferralAttribution;

  saveReferralAttribution(attribution);

  initPosthog();
  if (initialized) {
    posthog.capture("referral_link_opened", attribution);
  }

  return attribution;
};

export const capturePosthogEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => {
  initPosthog();
  if (!initialized || !event) return;
  posthog.capture(event, cleanProperties(properties));
};

export const captureAuthCompleted = (params: {
  authMethod?: string | null;
  isNewUser?: boolean;
  signupSource?: string | null;
}) => {
  initPosthog();
  if (!initialized) return;
  const referralAttribution =
    captureReferralAttributionFromUrl() || readStoredReferralAttribution();
  posthog.capture(
    "auth_completed",
    cleanProperties({
      auth_method: resolveAuthMethod(params.authMethod),
      is_new_user: Boolean(params.isNewUser),
      signup_source: resolveSignupSource(params.signupSource, referralAttribution),
    }),
  );
};

export const setPosthogPersonProperties = (
  properties?: Record<string, unknown>,
) => {
  initPosthog();
  if (!initialized) return;
  const payload = cleanProperties(properties);
  const client = posthog as any;
  if (typeof client.setPersonProperties === "function") {
    client.setPersonProperties(payload);
    return;
  }
  if (typeof client.people?.set === "function") {
    client.people.set(payload);
  }
};

export const setOnboardingStartedAt = (startedAtMs: number = Date.now()) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ONBOARDING_STARTED_AT_KEY, String(startedAtMs));
};

export const getOnboardingStartedAt = (): number | null => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(ONBOARDING_STARTED_AT_KEY);
  if (!raw) return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
};

export const clearOnboardingStartedAt = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(ONBOARDING_STARTED_AT_KEY);
};

const buildOnboardingCompletedKey = (brandId: string) =>
  `${ONBOARDING_COMPLETED_AT_PREFIX}:${brandId}`;

export const setBrandOnboardingCompletedAt = (
  brandId?: string | null,
  iso?: string | null,
) => {
  const normalizedBrandId = String(brandId || "").trim();
  if (typeof window === "undefined" || !normalizedBrandId) return;
  const value = String(iso || new Date().toISOString()).trim();
  if (!value) return;
  window.localStorage.setItem(buildOnboardingCompletedKey(normalizedBrandId), value);
};

export const getBrandOnboardingCompletedAt = (
  brandId?: string | null,
): string | null => {
  const normalizedBrandId = String(brandId || "").trim();
  if (typeof window === "undefined" || !normalizedBrandId) return null;
  return window.localStorage.getItem(buildOnboardingCompletedKey(normalizedBrandId));
};

export const getTimeSinceBrandOnboardingSeconds = (
  brandId?: string | null,
): number | null => {
  const raw = getBrandOnboardingCompletedAt(brandId);
  if (!raw) return null;
  const completedAt = new Date(raw);
  const completedMs = completedAt.getTime();
  if (!Number.isFinite(completedMs)) return null;
  const deltaSeconds = Math.floor((Date.now() - completedMs) / 1000);
  return Math.max(0, deltaSeconds);
};

export const isFirstDashboardView = (params: {
  userId?: string | null;
  brandId?: string | null;
  dashboard: string;
}) => {
  const userId = String(params.userId || "").trim();
  const brandId = String(params.brandId || "").trim();
  const dashboard = String(params.dashboard || "").trim().toLowerCase();
  if (typeof window === "undefined" || !userId || !brandId || !dashboard) {
    return false;
  }
  const key = `${DASHBOARD_FIRST_VIEW_PREFIX}:${userId}:${brandId}:${dashboard}`;
  const existing = window.localStorage.getItem(key);
  if (existing) return false;
  window.localStorage.setItem(key, new Date().toISOString());
  return true;
};

export const identifyPosthogUser = (params: {
  user: UserLike | null | undefined;
  signupSource?: string | null;
  authMethod?: string | null;
}) => {
  const user = params.user;
  if (!user?.id) return;
  const referralAttribution =
    captureReferralAttributionFromUrl() || readStoredReferralAttribution();
  initPosthog();
  if (!initialized) return;

  const brands = Array.isArray(user.brands) ? user.brands : [];
  const brandsCount = brands.length;
  const companyId = user.company?.id || user.company?.companyId || null;
  const country =
    brands.find((brand) => Boolean(brand?.country))?.country || "";
  const role =
    user.role ||
    (() => {
      const tier = String(user.tier || "").toLowerCase();
      if (tier === "collaborator") return "brand_member";
      if (tier === "company_member") return "company_member";
      return "company_admin";
    })();
  const authMethod = resolveAuthMethod(
    params.authMethod || user.auth_provider || user.authProvider,
  );
  const companyType =
    user.company?.company_type || inferCompanyType(brandsCount);
  const resolvedSignupSource = resolveSignupSource(
    params.signupSource,
    referralAttribution,
  );

  posthog.identify(user.id, cleanProperties({
    user_id: user.id,
    email: user.email || "",
    company_id: companyId,
    auth_method: authMethod,
    role,
    plan: user.tier || "trial",
    signup_source: resolvedSignupSource,
    country,
    company_type: companyType,
    brands_count: brandsCount,
    referral_code: referralAttribution?.referral_code,
    referral_partner: referralAttribution?.referral_partner,
    utm_source: referralAttribution?.utm_source,
    utm_medium: referralAttribution?.utm_medium,
    utm_campaign: referralAttribution?.utm_campaign,
    utm_term: referralAttribution?.utm_term,
    utm_content: referralAttribution?.utm_content,
    gclid: referralAttribution?.gclid,
    fbclid: referralAttribution?.fbclid,
    referral_landing_path: referralAttribution?.landing_path,
    referral_landing_url: referralAttribution?.landing_url,
    referrer_domain: referralAttribution?.referrer_domain,
    referral_first_seen_at: referralAttribution?.first_seen_at,
  }));

  if (companyId) {
    posthog.group("company", companyId, cleanProperties({
      company_id: companyId,
      company_type: companyType,
      brands_count: brandsCount,
    }));
  }
};

export const resetPosthog = () => {
  if (!initialized) return;
  posthog.reset();
};

